import { useCallback, useEffect, useMemo, useState } from "react";

import { useLocation, useParams, useSearchParams } from "react-router-dom";



import { useBreadcrumbs } from "../../context/breadcrumb/breadcrumb-context.tsx";

import { showError } from "../../utils/toast.ts";

import PageShell from "../../components/shared/page-shell/page-shell.tsx";

import { EmptyStateMessage } from "../../components/shared/empty-state-message/empty-state-message.tsx";

import { ResignationSubmittedCard } from "../../components/features/offboarding/components/resignation-submitted-card.tsx";

import { ClearanceActionModal } from "../../components/features/offboarding/modals/index.ts";

import { useOffboardingDetail } from "../../components/features/offboarding/hooks/index.ts";

import { mapPersonalDetailToOffboardingDisplay } from "../../components/features/offboarding/utils/map-employee-profile-display.ts";

import {

    getSectionForDepartment,

} from "../../components/features/offboarding/utils/offboarding-roles.ts";

import type { ClearanceDepartment } from "../../components/features/offboarding/components/clearance-summary/clearance-summary.tsx";

import type { OffboardingSectionType } from "../../components/features/offboarding/schemas/index.ts";

import { useEmployeeProfile } from "../../hooks/use-employee-profile.ts";

import { Spinner } from "../../components/ui/index.ts";
import { useSqeLabs } from "../../hooks/use-sqe-labs.ts";

import type { PageProps } from "../../types/page.ts";
import type { OffboardingSource } from "../../components/features/offboarding/types.ts";

import styles from "./offboard-l1-detail-page.module.css";

import type { OffboardingReviewRole } from "./offboard-l1-page.tsx";

const ROLE_ROUTE: Record<OffboardingReviewRole, string> = {
    EXECUTIVE: "/dashboard/offboardl1",
    IT: "/dashboard/offboardlIT",
    ADMIN: "/dashboard/offboardladmin",
    PAYROLL: "/dashboard/offboardlpayroll",
    HR: "/dashboard/offboardlhr",
    RM: "/dashboard/offboardlrm",
};

const ROLE_DEPARTMENT: Partial<Record<OffboardingReviewRole, ClearanceDepartment>> = {
    IT: "IT",
    ADMIN: "ADMIN",
    PAYROLL: "PAYROLL",
    HR: "HR",
    RM: "RM",
};

type OffboardL1DetailPageProps = PageProps & {
    reviewRole?: OffboardingReviewRole;
};


export default function OffboardL1DetailPage({
    title,
    pageMeta,
    reviewRole = "EXECUTIVE",
}: OffboardL1DetailPageProps) {

    const { offboardingId: offboardingIdParam } = useParams<{ offboardingId: string }>();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isSqe = useSqeLabs();
    const hasSourceTabs = reviewRole === "IT" || reviewRole === "ADMIN";
    const source: OffboardingSource = hasSourceTabs
        ? searchParams.get("employeeType") === "SQELABS" ? "SQELABS" : "REGULAR"
        : isSqe ? "SQELABS" : "REGULAR";

    const { setBreadcrumbs } = useBreadcrumbs();

    const [clearanceModalOpen, setClearanceModalOpen] = useState(false);

    const [activeSectionType, setActiveSectionType] =

        useState<OffboardingSectionType | null>(null);

    const breadcrumbRootLabel =
        (location.state as { breadcrumbTitle?: string } | null)?.breadcrumbTitle ||
        pageMeta?.title ||
        title ||
        "Offboarding Review";



    const offboardingId = useMemo(() => {

        const parsed = parseInt(offboardingIdParam ?? "", 10);

        return Number.isFinite(parsed) ? parsed : null;

    }, [offboardingIdParam]);



    const { detail, loading: detailLoading, error: detailError, refetch } =

        useOffboardingDetail(offboardingId, source);



    const employeeId = detail?.offboarding.employeeId ?? null;



    const {

        personalDetail,

        loading: profileLoading,

        error: profileError,

    } = useEmployeeProfile(employeeId);



    const employee = useMemo(() => {

        if (personalDetail == null) return null;

        return mapPersonalDetailToOffboardingDisplay(personalDetail);

    }, [personalDetail]);



    useEffect(() => {

        if (employee == null) return;



        setBreadcrumbs([
          { label: breadcrumbRootLabel, path: ROLE_ROUTE[reviewRole] },

          { label: employee.name },
        ]);



        return () => setBreadcrumbs([]);

    }, [setBreadcrumbs, employee, reviewRole, breadcrumbRootLabel]);



    useEffect(() => {

        if (detailError) showError(detailError);

    }, [detailError]);



    useEffect(() => {

        if (profileError) showError(profileError);

    }, [profileError]);



    const handleTakeClearanceAction = useCallback((department: ClearanceDepartment) => {

        setActiveSectionType(getSectionForDepartment(department));

        setClearanceModalOpen(true);

    }, []);



    const closeClearanceModal = useCallback(() => {

        setClearanceModalOpen(false);

        setActiveSectionType(null);

    }, []);



    if (offboardingId == null) {

        return (

            <PageShell>

                <EmptyStateMessage message="Invalid offboarding request." />

            </PageShell>

        );

    }



    if (detailLoading || profileLoading || detail == null || employee == null) {

        return (

            <PageShell>

                <Spinner />

            </PageShell>

        );

    }



    const hrClearancePending = detail.offboarding.status === "HR_CLEARANCE_PENDING";
    const routeDepartment = ROLE_DEPARTMENT[reviewRole];
    const routeDepartments = routeDepartment == null ? [] : [routeDepartment];
    const canReviewSubmittedForms = reviewRole === "EXECUTIVE" || reviewRole === "HR";
    const canViewSubmittedRequest =
        canReviewSubmittedForms && !detail.offboarding.isTermination;
    const canViewAllocatedAssets = reviewRole === "IT";
    const hasClearanceActions = routeDepartments.length > 0;
    const canViewCompletedClearanceForms =
        canReviewSubmittedForms || reviewRole !== "EXECUTIVE";



    return (

        <PageShell className={styles.pageRoot}>

            <ResignationSubmittedCard

                employee={employee}

                offboardingId={offboardingId}

                detail={detail}
                source={source}

                showWithdraw={false}

                showViewResignationForm={canViewSubmittedRequest}
                showViewExitInterviewForm={canReviewSubmittedForms}
                showAllocatedAssets={canViewAllocatedAssets}

                clearanceSummary={

                    canReviewSubmittedForms || hasClearanceActions

                        ? {

                              showActions: true,

                              viewerDepartments: hasClearanceActions ? routeDepartments : [],

                              canViewAllCompleted: canViewCompletedClearanceForms,

                              allOtherDeptsComplete: hrClearancePending,
                              exitInterviewAcknowledged:
                                  detail.offboarding.isAcknowledged === true,

                              onTakeAction: hasClearanceActions ? handleTakeClearanceAction : undefined,

                          }

                        : undefined

                }

                onWithdrawSuccess={() => {}}
                onExitInterviewSuccess={() => void refetch()}

            />



            {activeSectionType != null && (

                <ClearanceActionModal

                    open={clearanceModalOpen}

                    onClose={closeClearanceModal}

                    offboardingId={offboardingId}
                    source={source}

                    sectionType={activeSectionType}

                    onSuccess={() => void refetch()}

                />

            )}

        </PageShell>

    );

}


